import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, Pencil, Trash2, FolderOpen, Loader2, ExternalLink, FileText } from "lucide-react";
import { NdaBadge, NdaBanner } from "./NdaBits";

const CHUNK = 4 * 1024 * 1024;
const STATUSES = ["draft", "sent", "executed", "expired", "superseded"];

// Chunked upload to the client (not a bid) → complete with the metadata the person filled in.
async function uploadClientFile(clientId, file, meta, onPct) {
  const { data: { upload_id } } = await api.post(`/clients/${clientId}/uploads/init`, { filename: file.name, size: file.size, content_type: file.type });
  const chunks = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < chunks; i++) {
    const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
    await api.put(`/uploads/${upload_id}/chunk?index=${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
    onPct?.(Math.round(((i + 1) / chunks) * 100));
  }
  return (await api.post(`/clients/${clientId}/documents/complete`, { upload_id, filename: file.name, content_type: file.type || "application/octet-stream", ...meta })).data;
}

const emptyMeta = (folder) => ({ folder, name: "", status: folder === "NDA" ? "executed" : "draft", executed_at: "", expires_at: "", parties: "", signed_by: "", notes: "", paper: "" });

// Metadata form shared by upload and edit. Dates matter: an executed NDA with an expiry clears the gate.
function DocMetaDialog({ open, title, folders, initial, file, onClose, onSave, busy, pct }) {
  const [m, setM] = useState(initial);
  useEffect(() => { if (open) setM(initial); }, [open, initial]);
  const set = (k, v) => setM((p) => ({ ...p, [k]: v }));
  if (!open) return null;
  const isNda = m.folder === "NDA";
  const needDates = isNda && m.status === "executed";
  const ok = m.folder && (file || m.name) && (!needDates || (m.executed_at && m.expires_at));
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="client-doc-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{file ? <>Filing <b>{file.name}</b>. </> : null}{isNda ? "An executed NDA needs its signed and expiry dates — they drive the send gate and the 7 AM watch." : "Filed under GridForge Projects / Clients on Drive when storage is connected."}</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-slate-500">Folder
            <select value={m.folder} onChange={(e) => set("folder", e.target.value)} data-testid="client-doc-folder" className="mt-1 w-full border border-slate-200 rounded px-2 py-2 text-sm">
              {folders.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-500">Status
            <select value={m.status} onChange={(e) => set("status", e.target.value)} data-testid="client-doc-status" className="mt-1 w-full border border-slate-200 rounded px-2 py-2 text-sm">
              {STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-500 sm:col-span-2">Name
            <Input value={m.name} onChange={(e) => set("name", e.target.value)} placeholder={file ? file.name : "e.g. Mutual NDA 2026"} data-testid="client-doc-name" className="mt-1" />
          </label>
          <label className="text-xs text-slate-500">{isNda ? "Signed" : "Effective"} date{needDates && " *"}
            <Input type="date" value={m.executed_at || ""} onChange={(e) => set("executed_at", e.target.value)} data-testid="client-doc-executed-at" className="mt-1" />
          </label>
          <label className="text-xs text-slate-500">Expires{needDates && " *"}
            <Input type="date" value={m.expires_at || ""} onChange={(e) => set("expires_at", e.target.value)} data-testid="client-doc-expires-at" className="mt-1" />
          </label>
          {isNda && (
            <label className="text-xs text-slate-500">Paper
              <select value={m.paper || ""} onChange={(e) => set("paper", e.target.value)} data-testid="client-doc-paper" className="mt-1 w-full border border-slate-200 rounded px-2 py-2 text-sm">
                <option value="">—</option><option value="ours">Our paper</option><option value="theirs">Their paper</option>
              </select>
            </label>
          )}
          <label className="text-xs text-slate-500">Signed by
            <Input value={m.signed_by || ""} onChange={(e) => set("signed_by", e.target.value)} data-testid="client-doc-signed-by" className="mt-1" />
          </label>
          <label className="text-xs text-slate-500 sm:col-span-2">Parties (comma-separated)
            <Input value={Array.isArray(m.parties) ? m.parties.join(", ") : (m.parties || "")} onChange={(e) => set("parties", e.target.value)} data-testid="client-doc-parties" className="mt-1" />
          </label>
          <label className="text-xs text-slate-500 sm:col-span-2">Notes
            <Textarea rows={2} value={m.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="client-doc-notes" className="mt-1" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="client-doc-cancel">Cancel</Button>
          <Button onClick={() => onSave(m)} disabled={!ok || busy} data-testid="client-doc-save">
            {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />{pct != null ? `${pct}%` : "Saving…"}</> : (file ? "Upload and file" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_CLS = { executed: "text-emerald-700 bg-emerald-50 border-emerald-200", sent: "text-amber-700 bg-amber-50 border-amber-200", expired: "text-red-700 bg-red-50 border-red-200", superseded: "text-slate-500 bg-slate-50 border-slate-200", draft: "text-slate-600 bg-slate-50 border-slate-200" };

function DocRow({ d, onEdit, onDelete, canDelete }) {
  const dl = async () => {
    try {
      const r = await api.get(`/client-documents/${d.document_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = d.name; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download"); }
  };
  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`client-doc-${d.document_id}`}>
      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-black truncate">{d.name}</div>
        <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
          {d.executed_at && <span>Signed {fmtDate(d.executed_at)}</span>}
          {d.expires_at && <span>· Expires {fmtDate(d.expires_at)}</span>}
          {d.signed_by && <span>· {d.signed_by}</span>}
          {d.paper && <span>· {d.paper === "ours" ? "Our paper" : "Their paper"}</span>}
          <span>· Filed by {d.filed_by}{d.source === "claude" ? " via Claude" : ""}</span>
          {!d.has_file && <span className="text-amber-700">· No file (record only)</span>}
        </div>
      </div>
      <span className={`text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 border ${STATUS_CLS[d.status] || STATUS_CLS.draft}`} data-testid={`client-doc-status-${d.document_id}`}>{d.status}</span>
      {d.drive_link && <a href={d.drive_link} target="_blank" rel="noreferrer" title="Open in Drive" className="text-slate-400 hover:text-[#003E87]"><ExternalLink className="w-3.5 h-3.5" /></a>}
      {d.has_file && <button onClick={dl} title="Download" data-testid={`client-doc-download-${d.document_id}`} className="text-slate-400 hover:text-[#003E87]"><Download className="w-3.5 h-3.5" /></button>}
      <button onClick={() => onEdit(d)} title="Edit" data-testid={`client-doc-edit-${d.document_id}`} className="text-slate-400 hover:text-black"><Pencil className="w-3.5 h-3.5" /></button>
      {canDelete && <button onClick={() => onDelete(d)} title="Remove" data-testid={`client-doc-delete-${d.document_id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
    </div>
  );
}

// NDA / prequal / vendor status with the quick actions BD actually uses.
function ComplianceCard({ clientId, comp, docs, onChanged, isOwner }) {
  const [busy, setBusy] = useState("");
  const [elsewhere, setElsewhere] = useState(null);
  const [notReq, setNotReq] = useState(null);
  const s = comp.summary;
  const put = async (body, label) => {
    setBusy(label);
    try { await api.put(`/clients/${clientId}/compliance`, body); toast.success(label); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    finally { setBusy(""); }
  };
  const ndaDocs = docs.filter((d) => d.folder === "NDA");
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="client-compliance-card">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mr-auto">Compliance</div>
        <NdaBadge summary={s} testid="client-nda-badge" />
        <span className="text-xs text-slate-500" data-testid="client-prequal-status">Prequal: <b className="text-slate-700">{comp.prequal?.status || "none"}</b></span>
        <span className="text-xs text-slate-500" data-testid="client-vendor-status">Vendor setup: <b className="text-slate-700">{comp.vendor_setup?.status || "none"}</b></span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid="client-nda-paper">
        <span>NDA paper:</span>
        <select className="text-xs border border-slate-200 rounded px-2 py-1" value={comp.nda?.paper || "ours"} disabled={!!busy} data-testid="client-nda-paper-select"
          onChange={(e) => put({ nda: { ...(comp.nda || {}), status: comp.nda?.status || "none", paper: e.target.value } }, e.target.value === "theirs" ? "Their paper — file their executed NDA below" : "Power Ray's paper — the client link asks them to sign")}>
          <option value="ours">Ours — the client signs Power Ray's mutual NDA on their link</option>
          <option value="theirs">Theirs — their executed paper, filed by BD (the link does not ask)</option>
        </select>
        <span className="text-slate-400" data-testid="client-nda-gate-line">
          {["active", "expiring"].includes(s.state) ? `Client link opens straight to the documents (NDA on file${s.expires_at ? `, expires ${s.expires_at}` : ""}).`
            : s.state === "not_required" || s.state === "waived" ? "Client link opens without an NDA (waived / not required)."
            : comp.nda?.paper === "theirs" ? "Client link stays closed until their executed NDA is filed below."
            : s.state === "expired" ? "Client link shows the NDA renewal first." : "Client link shows Power Ray's mutual NDA first; the documents open once it is signed."}
        </span>
      </div>
      {s.state === "not_required" && <div className="text-xs text-slate-500" data-testid="client-nda-not-required-reason">NDA not required: {comp.nda_required_reason || "no reason recorded"}</div>}
      {comp.nda?.on_file_elsewhere && <div className="text-xs text-slate-500">Executed copy on file elsewhere — {comp.nda.on_file_elsewhere.who}: {comp.nda.on_file_elsewhere.why}</div>}
      <div className="flex flex-wrap gap-2">
        {["none", "expired", "sent", "waived"].includes(s.state) && comp.nda_required && (
          <>
            {s.state !== "sent" && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => put({ nda: { status: "sent" } }, "NDA marked sent")} data-testid="nda-mark-sent">Mark NDA sent</Button>}
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => setElsewhere({ who: "", why: "", executed_at: "", expires_at: "" })} data-testid="nda-on-file-elsewhere">Executed, on file elsewhere</Button>
            {s.state !== "waived" && isOwner && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => put({ nda: { status: "waived" } }, "NDA waived")} data-testid="nda-waive">Waive</Button>}
          </>
        )}
        {isOwner && comp.nda_required && <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setNotReq("")} data-testid="nda-not-required">NDA not required…</Button>}
        {isOwner && !comp.nda_required && <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => put({ nda_required: true }, "NDA required again")} data-testid="nda-required-again">Require an NDA again</Button>}
        {ndaDocs.length > 0 && s.state !== "active" && s.state !== "expiring" && (
          <select className="text-xs border border-slate-200 rounded px-2 py-1" defaultValue="" data-testid="nda-pick-doc"
            onChange={(e) => { const d = ndaDocs.find((x) => x.document_id === e.target.value); if (d) put({ nda: { status: "executed", document_id: d.document_id, executed_at: d.executed_at, expires_at: d.expires_at, paper: d.paper } }, "NDA linked"); }}>
            <option value="">Link a filed NDA…</option>
            {ndaDocs.map((d) => <option key={d.document_id} value={d.document_id}>{d.name}</option>)}
          </select>
        )}
        <select className="text-xs border border-slate-200 rounded px-2 py-1" value={comp.prequal?.status || "none"} data-testid="prequal-status-select"
          onChange={(e) => put({ prequal: { ...(comp.prequal || {}), status: e.target.value } }, "Prequal updated")}>
          {["none", "submitted", "approved", "expired"].map((v) => <option key={v} value={v}>Prequal: {v}</option>)}
        </select>
        <select className="text-xs border border-slate-200 rounded px-2 py-1" value={comp.vendor_setup?.status || "none"} data-testid="vendor-status-select"
          onChange={(e) => put({ vendor_setup: { status: e.target.value } }, "Vendor setup updated")}>
          {["none", "in_progress", "complete"].map((v) => <option key={v} value={v}>Vendor setup: {v.replace("_", " ")}</option>)}
        </select>
      </div>
      {elsewhere && (
        <Dialog open onOpenChange={(o) => { if (!o) setElsewhere(null); }}>
          <DialogContent className="max-w-md" data-testid="nda-elsewhere-dialog">
            <DialogHeader><DialogTitle>Executed NDA on file elsewhere</DialogTitle><DialogDescription>Record who confirmed it and why the copy isn't filed here. This is logged.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-slate-500 col-span-2">Who said so<Input value={elsewhere.who} onChange={(e) => setElsewhere({ ...elsewhere, who: e.target.value })} data-testid="nda-elsewhere-who" className="mt-1" /></label>
              <label className="text-xs text-slate-500 col-span-2">Why it isn't filed here<Input value={elsewhere.why} onChange={(e) => setElsewhere({ ...elsewhere, why: e.target.value })} data-testid="nda-elsewhere-why" className="mt-1" /></label>
              <label className="text-xs text-slate-500">Signed<Input type="date" value={elsewhere.executed_at} onChange={(e) => setElsewhere({ ...elsewhere, executed_at: e.target.value })} data-testid="nda-elsewhere-executed" className="mt-1" /></label>
              <label className="text-xs text-slate-500">Expires<Input type="date" value={elsewhere.expires_at} onChange={(e) => setElsewhere({ ...elsewhere, expires_at: e.target.value })} data-testid="nda-elsewhere-expires" className="mt-1" /></label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setElsewhere(null)}>Cancel</Button>
              <Button disabled={!elsewhere.who || !elsewhere.why || !elsewhere.expires_at} data-testid="nda-elsewhere-save"
                onClick={async () => { await put({ nda: { status: "executed", on_file_elsewhere: { who: elsewhere.who, why: elsewhere.why }, executed_at: elsewhere.executed_at || null, expires_at: elsewhere.expires_at } }, "NDA recorded as executed"); setElsewhere(null); }}>Record</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {notReq != null && (
        <Dialog open onOpenChange={(o) => { if (!o) setNotReq(null); }}>
          <DialogContent className="max-w-md" data-testid="nda-not-required-dialog">
            <DialogHeader><DialogTitle>NDA not required for this client</DialogTitle><DialogDescription>Public bids and municipal clients, for instance. Owner or admin only; the reason is kept.</DialogDescription></DialogHeader>
            <Textarea rows={2} value={notReq} onChange={(e) => setNotReq(e.target.value)} placeholder="Reason" data-testid="nda-not-required-reason" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setNotReq(null)}>Cancel</Button>
              <Button disabled={notReq.trim().length < 3} data-testid="nda-not-required-save" onClick={async () => { await put({ nda_required: false, nda_required_reason: notReq.trim() }, "NDA no longer required"); setNotReq(null); }}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default function ClientDocuments({ client, onCompliance }) {
  const { user } = useAuth();
  const clientId = client.client_id;
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(null);   // { file, folder } | { doc }
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(null);
  const fileRef = useRef(null);
  const folderRef = useRef("Other");
  const load = useCallback(() => api.get(`/clients/${clientId}/documents`).then((r) => { setData(r.data); onCompliance?.(r.data.compliance.summary); }).catch(() => toast.error("Could not load documents")), [clientId, onCompliance]);
  useEffect(() => { load(); }, [load]);
  const isOwner = ["owner", "admin"].includes(user?.role);
  const canDelete = ["owner", "admin", "chief_estimator", "business_development"].includes(user?.role);

  const pick = (folder) => { folderRef.current = folder; fileRef.current?.click(); };
  const onFile = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setPending({ file: f, folder: folderRef.current }); };

  const save = async (m) => {
    setBusy(true);
    const meta = { ...m, parties: typeof m.parties === "string" ? m.parties.split(",").map((x) => x.trim()).filter(Boolean) : (m.parties || []) };
    ["executed_at", "expires_at", "paper", "signed_by", "notes"].forEach((k) => { if (meta[k] === "") meta[k] = null; });
    if (!meta.name) delete meta.name;
    try {
      if (pending.file) { setPct(0); await uploadClientFile(clientId, pending.file, meta, setPct); toast.success(`Filed under ${meta.folder}`); }
      else { await api.put(`/clients/${clientId}/documents/${pending.doc.document_id}`, meta); toast.success("Document updated"); }
      setPending(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); setPct(null); }
  };
  const del = async (d) => {
    if (!window.confirm(`Remove "${d.name}" from ${client.name}?`)) return;
    try { await api.delete(`/clients/${clientId}/documents/${d.document_id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  if (!data) return <div className="text-sm text-slate-400 p-4">Loading…</div>;
  const byFolder = Object.fromEntries(data.folders.map((f) => [f, data.documents.filter((d) => d.folder === f)]));
  return (
    <div className="space-y-4" data-testid="client-documents">
      <NdaBanner summary={data.compliance.summary} what={client.name} testid="client-nda-banner" />
      <ComplianceCard clientId={clientId} comp={data.compliance} docs={data.documents} onChanged={load} isOwner={isOwner} />
      <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="client-doc-file-input" />
      <div className="grid md:grid-cols-2 gap-3">
        {data.folders.map((f) => (
          <div key={f} className="rounded-lg border border-slate-200 p-3" data-testid={`client-folder-${f.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase()}`}>
            <div className="flex items-center gap-2 mb-1">
              <FolderOpen className="w-4 h-4 text-[#003E87]" />
              <div className="text-sm font-semibold text-black flex-1">{f} <span className="text-slate-400 font-normal">({byFolder[f].length})</span></div>
              <Button size="sm" variant="ghost" onClick={() => pick(f)} data-testid={`client-folder-upload-${f.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase()}`}><Upload className="w-3.5 h-3.5 mr-1" /> Upload</Button>
              <Button size="sm" variant="ghost" onClick={() => setPending({ doc: null, folder: f, recordOnly: true })} title="Record without a file" data-testid={`client-folder-record-${f.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase()}`}>Record</Button>
            </div>
            {byFolder[f].length === 0 ? <div className="text-xs text-slate-400 py-1">Nothing filed.</div> : byFolder[f].map((d) => <DocRow key={d.document_id} d={d} onEdit={(doc) => setPending({ doc })} onDelete={del} canDelete={canDelete} />)}
          </div>
        ))}
      </div>
      <DocMetaDialog open={!!pending} folders={data.folders} busy={busy} pct={pct} onClose={() => !busy && setPending(null)}
        title={pending?.file ? `File in ${pending.folder}` : pending?.doc ? "Edit document" : `Record a ${pending?.folder || ""} document`}
        file={pending?.file || null}
        initial={pending?.doc ? { ...pending.doc, parties: (pending.doc.parties || []).join(", ") } : emptyMeta(pending?.folder || "Other")}
        onSave={pending?.recordOnly ? async (m) => {
          setBusy(true);
          const meta = { ...m, parties: typeof m.parties === "string" ? m.parties.split(",").map((x) => x.trim()).filter(Boolean) : [] };
          ["executed_at", "expires_at", "paper", "signed_by", "notes"].forEach((k) => { if (meta[k] === "") meta[k] = null; });
          try { await api.post(`/clients/${clientId}/documents`, meta); toast.success("Recorded"); setPending(null); load(); }
          catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
          finally { setBusy(false); }
        } : save} />
    </div>
  );
}
